// File Metadata:
// Filename: ShipmentTimeline.js
// Version: 1.00
// Created: 2025-03-25 23:38:03
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, 
         TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab'
import { Box, Paper, Typography, Tooltip } from '@mui/material'
import { format } from 'date-fns'
import { SHIPMENT_STATUS, PRIORITY_LEVELS, ACTIVITY_TYPES } from '../../../config/shipmentConstants'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'

// Map our custom colors to valid MUI color values
const getMuiColor = (statusColor) => {
    const colorMap = {
        'default': 'grey',
        'primary': 'primary',
        'success': 'success',
        'warning': 'warning',
        'info': 'info',
        'error': 'error'
    }
    return colorMap[statusColor] || 'grey'
}

const ShipmentTimeline = ({ statusHistory = [] }) => {
    const getEventDisplay = (event) => {
        // Handle assignment events specifically
        if (event.status === 'ASSIGNED' || event.status === 'UNASSIGNED') {
            const Icon = ACTIVITY_TYPES['ASSIGNED'].icon
            return {
                icon: <Icon />,
                color: event.status === 'ASSIGNED' ? 'primary' : 'default',
                label: event.status === 'ASSIGNED' ? 'Assigned' : 'Unassigned'
            }
        }

        // First check if it's a status update
        if (event.status.includes('STATUS_UPDATED')) {
            const status = SHIPMENT_STATUS[event.newValue]
            if (status) {
                const Icon = status.icon
                return {
                    icon: <Icon />,
                    color: getMuiColor(status.color),
                    label: `Status: ${status.label}`
                }
            }
        }

        // Then check if it's a priority update
        if (event.status.includes('PRIORITY_UPDATED')) {
            const priority = PRIORITY_LEVELS[event.newValue]
            if (priority) {
                return {
                    icon: <PriorityHighIcon />,
                    color: getMuiColor(priority.color),
                    label: `Priority: ${priority.label}`
                }
            }
        }

        // Then check if it's another activity type
        const activity = ACTIVITY_TYPES[event.status]
        if (activity) {
            const Icon = activity.icon
            return {
                icon: <Icon />,
                color: getMuiColor(activity.color),
                label: activity.label
            }
        }

        // Default fallback
        return {
            icon: null,
            color: 'grey',
            label: event.status
        }
    }

    const renderEventNote = (event) => {
        // For assignment events, use the note that was set in the controller
        if (event.status === 'ASSIGNED' || event.status === 'UNASSIGNED') {
            return event.note;
        }

        // Handle assignment events
        if (event.status === 'ASSIGNED') {
            if (event.newValue === 'none') {
                return 'Unassigned'
            }
            // Get username from changedBy field (already populated from backend)
            const assignedTo = event.changedBy?.username || 'Unknown user'
            return `Assigned to ${assignedTo}`
        }

        // Handle other events
        if (event.status.endsWith('_UPDATED') && event.oldValue && event.newValue) {
            return `Changed from "${event.oldValue}" to "${event.newValue}"`
        }
        return event.note
    }

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Activity Timeline</Typography>
            <Timeline>
                {statusHistory.map((event, index) => {
                    const { icon, color, label } = getEventDisplay(event)
                    
                    return (
                        <TimelineItem key={index}>
                            <TimelineOppositeContent color="text.secondary">
                                {format(new Date(event.timestamp), 'HH:mm - dd MMM')}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                                <TimelineDot color={color}>
                                    {icon}
                                </TimelineDot>
                                {index < statusHistory.length - 1 && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="body2">{label}</Typography>
                                    {event.note && (
                                        <Tooltip title={renderEventNote(event)} arrow placement="top">
                                            <Typography 
                                                variant="caption" 
                                                color="text.secondary"
                                                sx={{ 
                                                    maxWidth: 200,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                            >
                                                {renderEventNote(event)}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Box>
                            </TimelineContent>
                        </TimelineItem>
                    )
                })}
            </Timeline>
        </Paper>
    )
}

export default ShipmentTimeline
